import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { 
  Ship, Download, FileText, Trash2, Eye, RefreshCw, 
  ChevronLeft, ChevronRight, Filter, BarChart3, Users,
  TrendingUp, LogOut, Search, Mail, AlertTriangle, CheckCircle2,
  XCircle, Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toaster, toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Simple bar chart component
const SimpleBarChart = ({ data, height = 200 }) => {
  if (!data || data.length === 0) return <div className="text-center text-gray-400 py-8">Aucune donnée</div>;
  
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const barWidth = Math.max(8, Math.min(30, Math.floor(800 / data.length)));
  
  return (
    <div className="w-full overflow-x-auto">
      <div className="flex items-end gap-1 justify-center" style={{ height: `${height}px`, minWidth: `${data.length * (barWidth + 4)}px` }}>
        {data.map((item, idx) => (
          <div key={idx} className="flex flex-col items-center group relative">
            <div 
              className="bg-[#0B2A4A] hover:bg-[#1e3a5f] rounded-t transition-all cursor-pointer"
              style={{ 
                width: `${barWidth}px`,
                height: `${Math.max(4, (item.count / maxCount) * (height - 40))}px`
              }}
              title={`${item.date}: ${item.count} projets`}
            />
            <span className="text-[9px] text-gray-400 mt-1 rotate-45 origin-left whitespace-nowrap">
              {item.date?.slice(5)}
            </span>
            {/* Tooltip */}
            <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs p-2 rounded shadow-lg z-10 whitespace-nowrap">
              <div>{item.date}</div>
              <div>{item.count} projet(s)</div>
              {item.avg_score > 0 && <div>Score moy: {item.avg_score}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [activeTab, setActiveTab] = useState("projects");
  
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Chart data
  const [chartData, setChartData] = useState([]);
  const [chartPeriod, setChartPeriod] = useState("30days");
  const [chartLoading, setChartLoading] = useState(false);
  
  // Email data
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [emailPage, setEmailPage] = useState(1);
  const [emailTotalPages, setEmailTotalPages] = useState(1);
  const [emailLoading, setEmailLoading] = useState(false);
  
  // Pagination & filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    project_type: "",
    min_score: "",
    max_score: "",
    sort_by: "created_at",
    sort_order: "desc"
  });

  const authHeaders = useCallback(() => {
    return {
      "Authorization": "Basic " + btoa("admin:" + password),
      "Content-Type": "application/json"
    };
  }, [password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    try {
      const res = await fetch(`${API}/admin/stats`, {
        headers: authHeaders()
      });
      if (res.ok) {
        setAuthenticated(true);
        localStorage.setItem("admin_pwd", password);
      } else {
        setAuthError("Mot de passe incorrect");
      }
    } catch (err) {
      setAuthError("Erreur de connexion");
    }
  };

  const handleLogout = () => {
    setAuthenticated(false);
    setPassword("");
    localStorage.removeItem("admin_pwd");
  };

  // Check saved password on mount
  useEffect(() => {
    const saved = localStorage.getItem("admin_pwd");
    if (saved) {
      setPassword(saved);
      fetch(`${API}/admin/stats`, {
        headers: { "Authorization": "Basic " + btoa("admin:" + saved) }
      }).then(res => {
        if (res.ok) setAuthenticated(true);
        else localStorage.removeItem("admin_pwd");
      }).catch(() => localStorage.removeItem("admin_pwd"));
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/stats`, { headers: authHeaders() });
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  }, [authHeaders]);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "15",
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
      });
      if (filters.project_type) params.append("project_type", filters.project_type);
      if (filters.min_score) params.append("min_score", filters.min_score);
      if (filters.max_score) params.append("max_score", filters.max_score);

      const res = await fetch(`${API}/admin/projects?${params}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects);
        setTotalPages(data.pages);
        setTotal(data.total);
      }
    } catch (err) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [page, filters, authHeaders]);

  const fetchChartData = useCallback(async () => {
    setChartLoading(true);
    try {
      const res = await fetch(`${API}/admin/submissions-chart?period=${chartPeriod}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setChartData(data.data || []);
      }
    } catch (err) {
      console.error("Error fetching chart data:", err);
    } finally {
      setChartLoading(false);
    }
  }, [chartPeriod, authHeaders]);

  const fetchEmailLogs = useCallback(async () => {
    setEmailLoading(true);
    try {
      const res = await fetch(`${API}/admin/email-logs?page=${emailPage}&limit=10`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEmailLogs(data.logs || []);
        setEmailTotalPages(data.pages || 1);
      }
    } catch (err) {
      console.error("Error fetching email logs:", err);
    } finally {
      setEmailLoading(false);
    }
  }, [emailPage, authHeaders]);

  const fetchEmailStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/email-stats`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEmailStats(data);
      }
    } catch (err) {
      console.error("Error fetching email stats:", err);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (authenticated) {
      fetchStats();
      fetchProjects();
      fetchChartData();
      fetchEmailStats();
    }
  }, [authenticated, fetchStats, fetchProjects, fetchChartData, fetchEmailStats]);

  useEffect(() => {
    if (authenticated && activeTab === "emails") {
      fetchEmailLogs();
    }
  }, [authenticated, activeTab, emailPage, fetchEmailLogs]);

  useEffect(() => {
    if (authenticated) {
      fetchChartData();
    }
  }, [chartPeriod, authenticated, fetchChartData]);

  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API}/admin/export/csv`, { headers: authHeaders() });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `projects_export_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        toast.success("Export CSV téléchargé");
      }
    } catch (err) {
      toast.error("Erreur d'export");
    }
  };

  const handleDownloadPDF = async (projectId) => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/pdf?lang=fr`, { headers: authHeaders() });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `projet_${projectId.slice(0,8)}.pdf`;
        a.click();
        toast.success("PDF téléchargé");
      }
    } catch (err) {
      toast.error("Erreur de téléchargement");
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm("Supprimer ce projet ?")) return;
    try {
      const res = await fetch(`${API}/admin/projects/${projectId}`, {
        method: "DELETE",
        headers: authHeaders()
      });
      if (res.ok) {
        toast.success("Projet supprimé");
        fetchProjects();
        fetchStats();
      }
    } catch (err) {
      toast.error("Erreur de suppression");
    }
  };

  const getScoreBadge = (score) => {
    if (score >= 75) return <Badge className="bg-green-500">ADVANCED</Badge>;
    if (score >= 50) return <Badge className="bg-yellow-500">MID</Badge>;
    return <Badge className="bg-red-500">EARLY</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  const projectTypeLabels = {
    retrofit: "Retrofit",
    new_build: "Construction Neuve",
    hybrid: "Hybride",
    shore_power: "Alimentation Quai",
    battery_only: "Batterie Seule",
    feasibility: "Étude Faisabilité"
  };

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0B2A4A] to-[#1e3a5f] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#0B2A4A] rounded-xl flex items-center justify-center mx-auto mb-4">
              <Ship className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#0B2A4A] font-['Tomorrow']">Admin Dashboard</h1>
            <p className="text-gray-500 mt-2">Maritime Configurator</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez le mot de passe admin"
                className="w-full"
                data-testid="admin-password-input"
              />
            </div>
            {authError && <p className="text-red-500 text-sm">{authError}</p>}
            <Button type="submit" className="w-full bg-[#0B2A4A] hover:bg-[#0B2A4A]/90" data-testid="admin-login-btn">
              Se connecter
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-[#0B2A4A] text-white py-4 px-6 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Ship className="w-8 h-8" />
          <div>
            <h1 className="text-xl font-bold font-['Tomorrow']">Admin Dashboard</h1>
            <p className="text-sm text-white/70">Maritime Configurator</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={handleExportCSV} className="text-white border-white/30 hover:bg-white/10" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Alert for failed emails */}
        {emailStats && emailStats.recent_failures && emailStats.recent_failures.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800">
                {emailStats.recent_failures.length} email(s) non envoyé(s) ces 7 derniers jours
              </h3>
              <p className="text-sm text-red-600 mt-1">
                Vérifiez l'onglet "Emails" pour plus de détails sur les erreurs.
              </p>
            </div>
          </motion.div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Projets</p>
                  <p className="text-2xl font-bold text-[#0B2A4A]">{stats.total_projects}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Score Moyen</p>
                  <p className="text-2xl font-bold text-[#0B2A4A]">{stats.average_score}/100</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`p-3 rounded-lg ${emailStats?.total_failed > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                  <Mail className={`w-6 h-6 ${emailStats?.total_failed > 0 ? 'text-red-600' : 'text-green-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Emails Envoyés</p>
                  <p className="text-2xl font-bold text-[#0B2A4A]">
                    {emailStats?.total_sent || 0}
                    {emailStats?.total_failed > 0 && (
                      <span className="text-sm text-red-500 ml-2">({emailStats.total_failed} échec)</span>
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Taux Succès Email</p>
                  <p className="text-2xl font-bold text-[#0B2A4A]">{emailStats?.success_rate || 100}%</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="projects" className="data-[state=active]:bg-[#0B2A4A] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" /> Projets
            </TabsTrigger>
            <TabsTrigger value="chart" className="data-[state=active]:bg-[#0B2A4A] data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" /> Évolution
            </TabsTrigger>
            <TabsTrigger value="emails" className="data-[state=active]:bg-[#0B2A4A] data-[state=active]:text-white">
              <Mail className="w-4 h-4 mr-2" /> Emails
              {emailStats?.total_failed > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {emailStats.total_failed}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[150px]">
                    <label className="text-sm text-gray-500 mb-1 block">Type de projet</label>
                    <Select value={filters.project_type || "all"} onValueChange={(v) => { setFilters({...filters, project_type: v === "all" ? "" : v}); setPage(1); }}>
                      <SelectTrigger data-testid="filter-type">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="retrofit">Retrofit</SelectItem>
                        <SelectItem value="new_build">Construction Neuve</SelectItem>
                        <SelectItem value="hybrid">Hybride</SelectItem>
                        <SelectItem value="shore_power">Alimentation Quai</SelectItem>
                        <SelectItem value="battery_only">Batterie Seule</SelectItem>
                        <SelectItem value="feasibility">Étude Faisabilité</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <label className="text-sm text-gray-500 mb-1 block">Score min</label>
                    <Input 
                      type="number" 
                      min="0" max="100"
                      value={filters.min_score}
                      onChange={(e) => { setFilters({...filters, min_score: e.target.value}); setPage(1); }}
                      placeholder="0"
                      data-testid="filter-min-score"
                    />
                  </div>
                  <div className="w-24">
                    <label className="text-sm text-gray-500 mb-1 block">Score max</label>
                    <Input 
                      type="number" 
                      min="0" max="100"
                      value={filters.max_score}
                      onChange={(e) => { setFilters({...filters, max_score: e.target.value}); setPage(1); }}
                      placeholder="100"
                      data-testid="filter-max-score"
                    />
                  </div>
                  <div className="w-36">
                    <label className="text-sm text-gray-500 mb-1 block">Trier par</label>
                    <Select value={filters.sort_by} onValueChange={(v) => setFilters({...filters, sort_by: v})}>
                      <SelectTrigger data-testid="filter-sort">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="created_at">Date</SelectItem>
                        <SelectItem value="score">Score</SelectItem>
                        <SelectItem value="company">Entreprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" onClick={() => { fetchProjects(); fetchStats(); }} data-testid="refresh-btn">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Projects Table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Projets ({total})</span>
                  <span className="text-sm font-normal text-gray-500">Page {page}/{totalPages || 1}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-gray-500">Chargement...</div>
                ) : projects.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">Aucun projet trouvé</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-600">Date</th>
                          <th className="text-left p-3 font-medium text-gray-600">Entreprise</th>
                          <th className="text-left p-3 font-medium text-gray-600">Contact</th>
                          <th className="text-left p-3 font-medium text-gray-600">Type</th>
                          <th className="text-left p-3 font-medium text-gray-600">Score</th>
                          <th className="text-left p-3 font-medium text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projects.map((p) => (
                          <tr key={p.id} className="border-b hover:bg-gray-50" data-testid={`project-row-${p.id?.slice(0,8)}`}>
                            <td className="p-3 text-gray-600">{formatDate(p.created_at)}</td>
                            <td className="p-3 font-medium">{p.contact?.company_name}</td>
                            <td className="p-3">
                              <div>{p.contact?.contact_name}</div>
                              <div className="text-xs text-gray-500">{p.contact?.email}</div>
                            </td>
                            <td className="p-3">
                              <Badge variant="outline">{projectTypeLabels[p.project_type] || p.project_type}</Badge>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <span className="font-bold">{p.score?.total_score}</span>
                                {getScoreBadge(p.score?.total_score)}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex gap-1">
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button variant="ghost" size="sm" onClick={() => setSelectedProject(p)} data-testid={`view-${p.id?.slice(0,8)}`}>
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                    <DialogHeader>
                                      <DialogTitle>Détails du projet</DialogTitle>
                                    </DialogHeader>
                                    {selectedProject && (
                                      <div className="space-y-4 text-sm">
                                        <div className="grid grid-cols-2 gap-4">
                                          <div>
                                            <h4 className="font-semibold text-[#0B2A4A] mb-2">Client</h4>
                                            <p><strong>Entreprise:</strong> {selectedProject.contact?.company_name}</p>
                                            <p><strong>Contact:</strong> {selectedProject.contact?.contact_name}</p>
                                            <p><strong>Email:</strong> {selectedProject.contact?.email}</p>
                                            <p><strong>Téléphone:</strong> {selectedProject.contact?.phone}</p>
                                            <p><strong>Localisation:</strong> {selectedProject.contact?.location}</p>
                                          </div>
                                          <div>
                                            <h4 className="font-semibold text-[#0B2A4A] mb-2">Projet</h4>
                                            <p><strong>Type:</strong> {projectTypeLabels[selectedProject.project_type]}</p>
                                            <p><strong>Score:</strong> {selectedProject.score?.total_score}/100</p>
                                            <p><strong>Maturité:</strong> {selectedProject.score?.maturity_level}</p>
                                            <p><strong>Action:</strong> {selectedProject.score?.recommended_action?.replace(/_/g, ' ')}</p>
                                          </div>
                                        </div>
                                        <div>
                                          <h4 className="font-semibold text-[#0B2A4A] mb-2">Profil Opérationnel</h4>
                                          <p>{selectedProject.operational?.usage_description}</p>
                                        </div>
                                      </div>
                                    )}
                                  </DialogContent>
                                </Dialog>
                                <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(p.id)} data-testid={`pdf-${p.id?.slice(0,8)}`}>
                                  <FileText className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-${p.id?.slice(0,8)}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 p-4 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {page} sur {totalPages}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Chart Tab */}
          <TabsContent value="chart">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Évolution des soumissions
                  </CardTitle>
                  <Select value={chartPeriod} onValueChange={setChartPeriod}>
                    <SelectTrigger className="w-40">
                      <Calendar className="w-4 h-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7days">7 derniers jours</SelectItem>
                      <SelectItem value="30days">30 derniers jours</SelectItem>
                      <SelectItem value="90days">90 derniers jours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {chartLoading ? (
                  <div className="h-[250px] flex items-center justify-center text-gray-500">
                    Chargement...
                  </div>
                ) : (
                  <div>
                    <SimpleBarChart data={chartData} height={250} />
                    <div className="mt-4 text-center text-sm text-gray-500">
                      Total sur la période : <span className="font-semibold text-[#0B2A4A]">{chartData.reduce((sum, d) => sum + d.count, 0)} projets</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Emails Tab */}
          <TabsContent value="emails" className="space-y-6">
            {/* Email Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Emails envoyés</p>
                    <p className="text-2xl font-bold text-green-600">{emailStats?.total_sent || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Emails échoués</p>
                    <p className="text-2xl font-bold text-red-600">{emailStats?.total_failed || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Taux de succès</p>
                    <p className="text-2xl font-bold text-blue-600">{emailStats?.success_rate || 100}%</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Email Logs Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Historique des emails
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {emailLoading ? (
                  <div className="p-8 text-center text-gray-500">Chargement...</div>
                ) : emailLogs.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">Aucun email enregistré</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-600">Date</th>
                          <th className="text-left p-3 font-medium text-gray-600">Entreprise</th>
                          <th className="text-left p-3 font-medium text-gray-600">Destinataire</th>
                          <th className="text-left p-3 font-medium text-gray-600">Statut</th>
                          <th className="text-left p-3 font-medium text-gray-600">Détails</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailLogs.map((log, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 text-gray-600">{formatDate(log.sent_at)}</td>
                            <td className="p-3 font-medium">{log.company_name}</td>
                            <td className="p-3 text-gray-600">{log.recipient}</td>
                            <td className="p-3">
                              {log.status === 'sent' ? (
                                <Badge className="bg-green-500">
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Envoyé
                                </Badge>
                              ) : (
                                <Badge className="bg-red-500">
                                  <XCircle className="w-3 h-3 mr-1" /> Échec
                                </Badge>
                              )}
                            </td>
                            <td className="p-3 text-xs text-gray-500">
                              {log.status === 'sent' ? (
                                <span>ID: {log.message_id?.slice(0, 8)}...</span>
                              ) : (
                                <span className="text-red-500">{log.error?.slice(0, 50)}...</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Email Pagination */}
                {emailTotalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 p-4 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setEmailPage(p => Math.max(1, p - 1))}
                      disabled={emailPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {emailPage} sur {emailTotalPages}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setEmailPage(p => Math.min(emailTotalPages, p + 1))}
                      disabled={emailPage === emailTotalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
